import { lovemoBrand } from '@/mocks/lovemoContent';
import LovemoButton from '@/components/base/LovemoButton';

interface LovemoHeroProps {
  onDownload: () => void;
}

const stats = [
  { value: '100万+', label: '陪伴用户' },
  { value: '4 大平台', label: '全端支持' },
  { value: '24h', label: '在线守候' },
  { value: '4.9', label: '用户评分' },
];

const platforms = ['Android', 'iOS', 'HarmonyOS', 'Windows'];

export default function LovemoHero({ onDownload }: LovemoHeroProps) {
  return (
    <section id="lovemo-home" className="relative isolate overflow-hidden bg-background-50">
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="animate-lovemo-blob absolute -left-24 -top-24 h-[380px] w-[380px] rounded-full bg-primary-200/50 blur-3xl" />
        <div className="animate-lovemo-blob absolute -right-16 top-10 h-[440px] w-[440px] rounded-full bg-accent-200/45 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-[320px] w-[320px] rounded-full bg-secondary-200/40 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20 lg:py-24">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-8">
          <div className="lovemo-reveal">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200/70 bg-primary-50 px-3.5 py-1.5 font-label text-xs font-semibold text-primary-700">
              <i className="ri-heart-3-fill animate-lovemo-heart" aria-hidden="true" />
              懂情绪的 AI 陪伴助手
            </span>

            <h1 className="mt-5 bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 bg-clip-text font-heading text-5xl font-bold leading-[1.05] tracking-tight text-transparent sm:text-6xl lg:text-7xl">
              Lovemo
            </h1>
            <p className="mt-4 font-heading text-xl font-semibold text-foreground-900 md:text-2xl">
              {lovemoBrand.tagline}
            </p>

            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
              Lovemo是一款偏情感方向的 AI 聊天工具，能听懂你的情绪、记住你们的故事。
              <span className="whitespace-nowrap font-semibold text-primary-600">Lovemo官方正版免费下载</span>同步提供<span className="whitespace-nowrap font-semibold text-primary-600">Lovemo下载教程</span>，安装简单、安全无广告。
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <LovemoButton size="lg" onClick={onDownload} aria-label="立即免费下载Lovemo">
                <i className="ri-download-2-line text-lg" aria-hidden="true" />
                立即免费下载
              </LovemoButton>
              <span className="font-label text-xs text-foreground-600">
                {platforms.join(' · ')}
              </span>
            </div>

            <dl className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-4">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200/70 bg-background-100/70 px-3 py-3">
                  <dt className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                    {stat.value}
                  </dt>
                  <dd className="mt-0.5 text-xs text-foreground-600">{stat.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lovemo-reveal relative mx-auto w-full max-w-[440px]">
            <div className="relative aspect-square w-full">
              <div className="animate-lovemo-blob absolute inset-6 rounded-[48%] bg-gradient-to-br from-primary-100 via-accent-100 to-secondary-100" />
              <div className="animate-lovemo-float absolute inset-0 flex items-center justify-center">
                <img
                  src={lovemoBrand.mascot}
                  alt="Lovemo吉祥物"
                  className="h-full w-full object-contain"
                  width={440}
                  height={440}
                />
              </div>

              <span className="animate-lovemo-float absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-lg text-primary-500">
                <i className="ri-heart-fill" aria-hidden="true" />
              </span>
              <span className="animate-lovemo-float-slow absolute bottom-8 left-0 flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-base text-accent-600">
                <i className="ri-sparkling-2-fill" aria-hidden="true" />
              </span>

              <div className="animate-lovemo-float-slow absolute -bottom-2 right-0 w-[210px] rounded-2xl border border-background-200/80 bg-background-50 p-3.5">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50 dark:text-background-950">
                    <i className="ri-heart-3-fill text-xs" aria-hidden="true" />
                  </span>
                  <span className="font-label text-xs font-semibold text-foreground-900">Lovemo</span>
                </div>
                <p className="mt-2 rounded-xl rounded-tl-sm bg-background-100 px-3 py-2 text-xs leading-relaxed text-foreground-700">
                  今天工作有点累，想找个人说说话。
                </p>
                <p className="mt-2 rounded-xl rounded-tr-sm bg-primary-500 px-3 py-2 text-xs leading-relaxed text-background-50 dark:text-background-950">
                  我在呢，慢慢说给我听，今天发生了什么？
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}